import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { CollaboratorRoleEnum } from "@models/collaborator";
import { EventSummary } from "@models/event-summary";
import { SkeletonModule } from "primeng/skeleton";
import { TabsModule } from "primeng/tabs";
import { catchError, concat, map, of, shareReplay, switchMap, tap } from "rxjs";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { getParams } from "../../../utils/utils";
import { EventsManagementService } from "../../services/events-management.service";
import { DataCardComponent } from "../../shared/components/cards/data-card/data-card.component";
import { EventCardComponent } from "../../shared/components/cards/event-card/event-card.component";
import { SectionHeaderComponent } from "../../shared/section-header/section-header.component";
import { BatchesReportTableComponent } from "./batches-report-table/batches-report-table.component";
import {
  CancelEventDialogComponent,
  CancelEventDialogData,
} from "./cancel-event-dialog/cancel-event-dialog.component";
import { EmployeesTabComponent } from "./employees-tab/employees-tab.component";
import { GuestsTabComponent } from "./guests-tab/guests-tab.component";
import { ParticipantsTabComponent } from "./participants-tab/participants-tab.component";
import { ProducerCouponsPageComponent } from "../producer-coupons-page/producer-coupons-page.component";
import { ReportOverviewComponent } from "./report-overview/report-overview.component";
import { SalesSummaryTabComponent } from "./sales-summary-tab/sales-summary-tab.component";
import { ValidatedSummaryTabComponent } from "./validated-summary-tab/validated-summary-tab.component";
import { MarketingPanelComponent } from "./marketing/marketing-panel.component";
import { PdvSessionsTabComponent } from "./pdv-sessions-tab/pdv-sessions-tab.component";
@Component({
  selector: "app-producer-event-page",
  imports: [
    CommonModule,
    AppLayoutComponent,
    TabsModule,
    MatButtonModule,
    MatMenuModule,
    SkeletonModule,
    RouterModule,
    SectionHeaderComponent,
    DataCardComponent,
    BatchesReportTableComponent,
    ReportOverviewComponent,
    ValidatedSummaryTabComponent,
    ParticipantsTabComponent,
    ProducerCouponsPageComponent,
    EmployeesTabComponent,
    GuestsTabComponent,
    RouterModule,
    EventCardComponent,
    PageHeaderComponent,
    ButtonComponent,
    SalesSummaryTabComponent,
    MarketingPanelComponent,
    PdvSessionsTabComponent,
  ],
  templateUrl: "./producer-event-page.component.html",
  styleUrl: "./producer-event-page.component.scss",
})
export class ProducerEventPageComponent {
  private readonly _dialog = inject(MatDialog);
  private readonly _eventService = inject(EventsManagementService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  eventId: number | null = null;

  // Aba ativa do relatório. Pode vir por query param (ex.: ?tab=5 a partir
  // de uma notificação de colaborador aceito). Default: Visão 360 ("0").
  activeTab = this._route.snapshot.queryParamMap.get("tab") ?? "0";

  event$ = getParams("id").pipe(
    tap((eventId) => (this.eventId = eventId)),
    switchMap((eventId) =>
      concat(
        of(null).pipe(tap(() => (this.eventId = eventId))),
        this._eventService.getMyEventById(eventId),
      ),
    ),
    tap((event) => {
      if (!event) return;
      if (event.role === CollaboratorRoleEnum.PDV_PORTARIA_COORDINATOR) {
        if (!["5", "8"].includes(this.activeTab)) this.activeTab = "5";
        return;
      }
      if (event.role === CollaboratorRoleEnum.MARKETING) {
        if (event.event_type !== "free") this.activeTab = "7";
        return;
      }
      this.adjustTabForFreeEvent(event);
    }),
    shareReplay(1),
  );

  /**
   * Em evento gratuito as abas "Visão 360" (0), "Resumo Vendas" (1) e
   * "Cupons de Desconto" (6) são ocultadas. Em gratuito privado, "Pedidos"
   * (3) também some. Se a aba ativa apontar para uma oculta, vai para
   * "Relatório Validados".
   */
  private adjustTabForFreeEvent(event: EventInfoResponseBody): void {
    const isFree = event.event_type === "free";
    if (!isFree) {
      return;
    }

    const hiddenTabs = new Set(["0", "1", "6"]);
    if (event.is_private) {
      hiddenTabs.add("3");
    }

    if (hiddenTabs.has(this.activeTab)) {
      this.activeTab = "2";
    }
  }

  batches$ = this.event$.pipe(
    switchMap((event) => {
      if (!event || this.hasRestrictedReports(event)) return of([]);
      return this._eventService
        .getEventSalesByBatches(Number(event.id))
        .pipe(
          map((batches) =>
            batches.sort((a, b) => a.batch_index - b.batch_index),
          ),
        );
    }),
  );

  summary$ = this.event$.pipe(
    switchMap((event) => {
      if (!event || this.hasRestrictedReports(event)) return of(null);
      return concat(
        of(null),
        this._eventService.getEventSummary(Number(event.id)),
      );
    }),
  );

  // Total vendido pelos vendedores (soma de todos os vendedores do evento).
  sellerTotal$ = this.event$.pipe(
    switchMap((event) => {
      if (!event || this.hasRestrictedReports(event)) return of(null);
      return concat(
        of(null),
        this._eventService
          .getSellerSalesSummary(Number(event.id), 0, 1000)
          .pipe(
            map((page) => ({
              value: page.data.reduce(
                (total, seller) => total + Number(seller.total_revenue),
                0,
              ),
              quantity: page.data.reduce(
                (total, seller) => total + seller.tickets_count,
                0,
              ),
            })),
            catchError(() => of({ value: 0, quantity: 0 })),
          ),
      );
    }),
  );

  myEvents$ = this._eventService.listMyEventsMerged();

  private hasRestrictedReports(event: EventInfoResponseBody) {
    return (
      event.role === CollaboratorRoleEnum.MARKETING ||
      event.role === CollaboratorRoleEnum.PDV_PORTARIA_COORDINATOR
    );
  }

  selectEvent(event: Event) {
    const eventId = (event.target as HTMLInputElement).value;
    this._router.navigateByUrl(`producer-area/event/${eventId}`);
  }

  goToEventPage() {
    this._router.navigateByUrl(`/event/${this.eventId}`);
  }

  getValidSales(summary: EventSummary) {
    return (
      Number(summary.valid_sales.value) +
      Number(summary.receivable_sales.value) -
      Number(summary.refunded.value)
    );
  }

  totalSalesValue(summary: EventSummary): number {
    return Number(
      summary.valid_sales.gross_value ?? summary.valid_sales.value ?? 0,
    );
  }

  couponDiscountValue(summary: EventSummary): number {
    return Number(
      summary.coupon_discounts?.value ??
        summary.valid_sales.discount_value ??
        0,
    );
  }

  pdvPortariaValue(summary: EventSummary): number {
    return Number(
      summary.pdv_portaria?.value ?? summary.valid_sales.pdv_fee_value ?? 0,
    );
  }

  pdvPortariaDebitValue(summary: EventSummary): number {
    const fee = Math.abs(this.pdvPortariaValue(summary));
    return fee === 0 ? 0 : -fee;
  }

  financialContributionValue(summary: EventSummary): number {
    return Number(summary.financial_contribution?.value ?? 0);
  }

  cancelEvent(isFreeEvent = false) {
    this._dialog.open(CancelEventDialogComponent, {
      width: "500px",
      data: { eventId: this.eventId, isFreeEvent } as CancelEventDialogData,
    });
  }
}
